import {useMutation, useQueryClient} from '@tanstack/react-query';
import {HTTPError} from 'ky';
import {useTranslation} from 'tolgee';

import {Model} from '~redux/types/models';
import {selectCurrentModelId} from '~redux/reducers/annotatorReducer';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {decreaseGlobalLoadingIndicator, increaseBlockingLoadingIndicator} from '~redux/actions/globalActions';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {showNotification} from '~components/common/Notification';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {showApiErrorNotification} from './api';
import {
  CloneModelPayload,
  CreateModelPayload,
  deleteModel,
  deleteModelInferenceCriteria,
  deleteModelLabel,
  ModelsResponse,
  postAdvancedConfiguration,
  postAugmentationPreview,
  postCloneModel,
  postModel,
  postModelTrainingConfig,
  postPreprocessingPreview,
  postUnpublishModel,
  putArchiveModel,
  putModel,
  putModelInferenceCriteria,
  putUnarchiveModel,
  SingleModelResponse,
  UpdateModelPayload,
} from './models';
import {ModelsQueryKeys} from './models.queries';

export function useCreateModelMutation() {
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();

  return useMutation<ModelsResponse, HTTPError, CreateModelPayload>({
    mutationFn: (newModel: CreateModelPayload) => postModel(projectId, newModel),
    onSuccess: (updatedModels) => {
      queryClient.setQueryData(ModelsQueryKeys.allModels(projectId), updatedModels);
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.modelCounts(projectId)});
    },
    onError: showApiErrorNotification,
  });
}

function updateSingleModel(allModels: Model[] | undefined, updatedModel: Model): Model[] {
  if (!allModels) {
    return [];
  }
  return allModels.map((model) => {
    if (model.id === updatedModel.id) {
      return updatedModel;
    } else {
      return model;
    }
  });
}

export function useUpdateModelMutation() {
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();

  return useMutation<SingleModelResponse, HTTPError, UpdateModelPayload>({
    mutationFn: (updatedModel: UpdateModelPayload) => putModel(projectId, updatedModel),
    onSuccess: (updatedModel) => {
      queryClient.setQueryData<Model[]>(ModelsQueryKeys.allModels(projectId), (allModels) =>
        updateSingleModel(allModels, updatedModel),
      );
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.modelCounts(projectId)});
    },
    onError: showApiErrorNotification,
  });
}

export function useCloneModelMutation() {
  const {t} = useTranslation('models');
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation<Model, HTTPError, {modelId: string; payload: CloneModelPayload}>({
    mutationFn: ({modelId, payload}) => postCloneModel({projectId, modelId, payload}),
    onMutate: () => {
      dispatch(increaseBlockingLoadingIndicator());
    },
    onSuccess: (clonedModel) => {
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.allModels(projectId)});
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.modelCounts(projectId)});
      showNotification({
        message: t('cloneModel.success', {name: clonedModel.name}),
        severity: 'success',
        id: `clone-model-success-${clonedModel.id}`,
      });
    },
    onError: showApiErrorNotification,
    onSettled: () => {
      dispatch(decreaseGlobalLoadingIndicator());
    },
  });
}

export function useDeleteModelLabelMutation() {
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();

  return useMutation<SingleModelResponse, HTTPError, {modelId: string; labelId: string}>({
    mutationFn: ({modelId, labelId}) => deleteModelLabel(projectId, modelId, labelId),
    onSuccess: (updatedModel) => {
      queryClient.setQueryData<Model[]>(ModelsQueryKeys.allModels(projectId), (allModels) =>
        updateSingleModel(allModels, updatedModel),
      );
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.modelCounts(projectId)});
    },
    onError: showApiErrorNotification,
  });
}

export function useDeleteModelMutation() {
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();

  return useMutation<Model[], HTTPError, {modelId: string}>({
    mutationFn: ({modelId}) => deleteModel(projectId, modelId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.allModels(projectId)});
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.modelCounts(projectId)});
    },
    onError: showApiErrorNotification,
  });
}
export function useCreateTrainingConfigMutation() {
  const {t} = useTranslation('models');
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();
  const queryClient = useQueryClient();

  return useMutation<
    ModelTrainingConfigurationResponse,
    HTTPError,
    {newTrainingConfig: ModelTrainingConfigurationPayload}
  >({
    mutationFn: ({newTrainingConfig}) => postModelTrainingConfig(projectId, currentModel!.id, newTrainingConfig),
    onSuccess: (updatedTrainingConfig) => {
      queryClient.setQueryData<ModelTrainingConfigurationResponse>(
        ModelsQueryKeys.modelTrainingConfiguration(projectId, currentModel?.id),
        updatedTrainingConfig,
      );
      showNotification({message: t('successModelUpdated'), severity: 'success'});

      if (updatedTrainingConfig.warning_messages && updatedTrainingConfig.warning_messages.length > 0) {
        updatedTrainingConfig.warning_messages.forEach((warning) => {
          showNotification({message: warning, severity: 'warning'});
        });
      }
    },
    onError: showApiErrorNotification,
  });
}

export function useUnpublishModelMutation() {
  const {t} = useTranslation('models');
  const {currentModel} = useCurrentModel();
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();

  return useMutation<{success: boolean}, HTTPError>({
    mutationFn: () =>
      postUnpublishModel({
        projectId,
        modelId: currentModel?.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.allModels(projectId)});
      // Trigger finetune page disabling immediately by refetching the publishing/unpublishing pipeline state.
      // These queries drive `usePublishingPipelineStatus` inside `FinetuneThresholdContext`.
      queryClient.invalidateQueries({queryKey: ['automl', 'latestPublishingPipeline', projectId]});
      queryClient.invalidateQueries({queryKey: ['automl', 'publishingPipelineStatus', projectId]});

      showNotification({
        message: t('finetunePage.latestModelPublished.unpublishInProgressNotification'),
        severity: 'success',
        id: 'unpublishModelSuccess',
      });
    },
    onError: showApiErrorNotification,
  });
}

export function useArchiveModelMutation() {
  const {t} = useTranslation('models');
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();

  return useMutation<{success: boolean}, HTTPError, {modelId: string}>({
    mutationFn: ({modelId}) => putArchiveModel({projectId, modelId}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.allModels(projectId)});
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.modelCounts(projectId)});
      showNotification({
        message: t('archiveModel.archivedSuccess'),
        severity: 'success',
        id: 'archiveModelSuccess',
      });
    },
    onError: showApiErrorNotification,
  });
}

export function useUnarchiveModelMutation() {
  const {t} = useTranslation('models');
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();

  return useMutation<{success: boolean}, HTTPError, {modelId: string}>({
    mutationFn: ({modelId}) => putUnarchiveModel({projectId, modelId}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.allModels(projectId)});
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.modelCounts(projectId)});
      showNotification({
        message: t('archiveModel.restoredSuccess'),
        severity: 'success',
        id: 'unarchiveModelSuccess',
      });
    },
    onError: showApiErrorNotification,
  });
}

export function useCreateAdvancedConfigMutation({
  onSuccess,
  onError,
}: {
  onSuccess: (data: ModelAdvancedConfigurationResponse) => void;
  onError: (error: HTTPError) => void;
}) {
  const {t} = useTranslation('models');
  const projectId = useAppSelector(selectCurrentProjectId);
  const currentModelId = useAppSelector(selectCurrentModelId);
  const dispatch = useAppDispatch();

  const queryClient = useQueryClient();

  return useMutation<
    ModelAdvancedConfigurationResponse,
    HTTPError,
    {newAdvancedConfig: ModelAdvancedConfigurationPayload}
  >({
    mutationFn: ({newAdvancedConfig}) =>
      postAdvancedConfiguration({
        projectId,
        modelId: currentModelId!,
        advancedConfig: newAdvancedConfig,
      }),
    onMutate: () => {
      dispatch(increaseBlockingLoadingIndicator());
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.combinedPreview(projectId, currentModelId)});
      queryClient.setQueryData(ModelsQueryKeys.currentModelAdvancedConfiguration(projectId, currentModelId), data);
      showNotification({
        message: t('successModelUpdated'),
        severity: 'success',
      });

      if (data.warning_messages && data.warning_messages.length > 0) {
        data.warning_messages.forEach((warning) => {
          showNotification({message: warning, severity: 'warning'});
        });
      }
      dispatch(decreaseGlobalLoadingIndicator());
      onSuccess(data);
    },
    onError: (data) => {
      showApiErrorNotification(data);
      dispatch(decreaseGlobalLoadingIndicator());
      onError(data);
    },
  });
}

export function useUpdateModelInferenceCriteriaMutation() {
  const {t} = useTranslation('models');
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();
  const queryClient = useQueryClient();

  return useMutation<ModelInferenceCriteriaResponse, HTTPError, {inferenceCriteria: ModelInferenceCriteriaPayload}>({
    mutationFn: ({inferenceCriteria}) =>
      putModelInferenceCriteria({
        projectId,
        modelId: currentModel!.id,
        inferenceCriteria,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(ModelsQueryKeys.modelInferenceCriteria(projectId, currentModel!.id), data);
      showNotification({
        message: t('successModelUpdated'),
        severity: 'success',
      });
    },
    onError: showApiErrorNotification,
  });
}

export function useDeleteModelInferenceCriteriaMutation() {
  const {t} = useTranslation('models');
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();
  const queryClient = useQueryClient();

  return useMutation<void, HTTPError>({
    mutationFn: () => deleteModelInferenceCriteria(projectId, currentModel!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ModelsQueryKeys.modelInferenceCriteria(projectId, currentModel!.id)});
      showNotification({
        message: t('successModelUpdated'),
        severity: 'success',
      });
    },
    onError: showApiErrorNotification,
  });
}

export function usePreprocessingPreviewMutation() {
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();

  return useMutation<PreprocessingPreviewResponse, HTTPError, {preprocessing: JsonObject}>({
    mutationFn: ({preprocessing}) =>
      postPreprocessingPreview({
        projectId,
        modelId: currentModel!.id,
        preprocessing,
      }),
    onError: showApiErrorNotification,
  });
}

export function useAugmentationPreviewMutation() {
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();

  return useMutation<AugmentationPreviewResponse, HTTPError, {augmentation: JsonObject[]}>({
    mutationFn: ({augmentation}) =>
      postAugmentationPreview({
        projectId,
        modelId: currentModel!.id,
        augmentation,
      }),
    onError: showApiErrorNotification,
  });
}
